import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { FloatingCard } from '@tyro/core/floating-card';
import { useDisclosure } from '@tyro/core/hooks';
import { StarSparklesIcon } from '@tyro/icons';
import { AnimatePresence, m } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useTyroneConversation } from '../../../../api/tyrone/conversation';
import { IconButtonAnimate } from '../../../icon-button-animate';
import { ConversationList } from './conversation-list';
import { ConversationView } from './conversation-view';
import { PopoverHeader } from './popover-header';
import type { TyroneMessageThread, TyroneView } from './types';

const slideVariants = {
  enterFromRight: {
    x: '100%',
    opacity: 0,
  },
  enterFromLeft: {
    x: '-100%',
    opacity: 0,
  },
  center: {
    x: 0,
    opacity: 1,
  },
  exitToLeft: {
    x: '-100%',
    opacity: 0,
  },
  exitToRight: {
    x: '100%',
    opacity: 0,
  },
};

// Other features open Tyrone with a seeded prompt by dispatching this event
// on `window`:
//
//   window.dispatchEvent(new CustomEvent('tyrone:open', {
//     detail: { prompt, context: { timetableId } },
//   }))
//
// Listening here keeps the popover free of cross-feature dependencies.
const TYRONE_OPEN_EVENT = 'tyrone:open';

export interface TyroneOpenEventDetail {
  prompt?: string;
  context?: {
    timetableId?: number;
  };
}

export function TyronePopover() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentView, setCurrentView] = useState<TyroneView>('list');
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null,
  );
  const [messageThread, setMessageThread] = useState<TyroneMessageThread>({
    conversationId: undefined,
    messages: [],
  });
  const [seededPrompt, setSeededPrompt] = useState<string | undefined>();
  const [seededPromptVersion, setSeededPromptVersion] = useState(0);
  const [pageContext, setPageContext] =
    useState<TyroneOpenEventDetail['context']>(undefined);

  const { data: conversationHistory } = useTyroneConversation(
    selectedConversationId,
  );

  const initialHeight = useMemo(() => window.innerHeight - 16, [isOpen]);

  useEffect(() => {
    if (selectedConversationId && conversationHistory?.messages) {
      setMessageThread({
        conversationId: selectedConversationId,
        messages: conversationHistory.messages.flatMap((msg) => [
          { id: `${msg.id}-prompt`, by: 'user' as const, text: msg.prompt },
          { id: `${msg.id}-response`, by: 'ai' as const, text: msg.response },
        ]),
      });
    }
  }, [selectedConversationId, conversationHistory]);

  // Clear any seeded-prompt state so it can't re-fire when the conversation
  // view is unmounted (back to list) and later remounted for a different chat.
  const clearSeeded = () => {
    setSeededPrompt(undefined);
    setPageContext(undefined);
  };

  const handleSelectConversation = (
    conversationId: string,
    initialMessage: string,
  ) => {
    clearSeeded();
    setSelectedConversationId(conversationId);
    setConversationTitle(initialMessage);
    setMessageThread({
      conversationId,
      messages: [],
    });
    setCurrentView('conversation');
  };

  const handleNewConversation = () => {
    clearSeeded();
    setSelectedConversationId(null);
    setConversationTitle(null);
    setMessageThread({
      conversationId: undefined,
      messages: [],
    });
    setCurrentView('conversation');
  };

  const handleBackToList = () => {
    clearSeeded();
    setCurrentView('list');
  };

  const handleClose = () => {
    onClose();
    setCurrentView('list');
    setSelectedConversationId(null);
    setConversationTitle(null);
    setSeededPrompt(undefined);
    setPageContext(undefined);
    setMessageThread({
      conversationId: undefined,
      messages: [],
    });
  };

  // Allow other parts of the app to open the popover into a fresh
  // conversation — used by the custom-report editor's empty-state CTA.
  useEffect(() => {
    function onOpenEvent() {
      setSelectedConversationId(null);
      setConversationTitle(null);
      setMessageThread({ conversationId: undefined, messages: [] });
      setCurrentView('conversation');
      onOpen();
    }
    window.addEventListener('tyro:open-tyrone', onOpenEvent);
    return () => window.removeEventListener('tyro:open-tyrone', onOpenEvent);
  }, [onOpen]);

  // Features open Tyrone with a seeded prompt + page context via this event.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<TyroneOpenEventDetail>).detail;
      setSelectedConversationId(null);
      setConversationTitle(detail?.prompt ?? null);
      setMessageThread({ conversationId: undefined, messages: [] });
      setSeededPrompt(detail?.prompt);
      setSeededPromptVersion((version) => version + 1);
      setPageContext(detail?.context);
      setCurrentView('conversation');
      onOpen();
    };
    window.addEventListener(TYRONE_OPEN_EVENT, handler);
    return () => window.removeEventListener(TYRONE_OPEN_EVENT, handler);
  }, [onOpen]);

  return (
    <>
      <Tooltip title="Tyrone">
        <IconButtonAnimate
          color={isOpen ? 'primary' : 'default'}
          onClick={onOpen}
          sx={{ width: 40, height: 40 }}
          aria-label="Tyrone"
        >
          <StarSparklesIcon />
        </IconButtonAnimate>
      </Tooltip>
      <FloatingCard
        open={isOpen}
        onClose={handleClose}
        initialHeight={initialHeight}
        initialPosition={{
          bottom: 8,
          right: 8,
        }}
        anchor="right"
        CardProps={{
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <PopoverHeader
          onClose={handleClose}
          showBackButton={currentView === 'conversation'}
          onBack={handleBackToList}
          onNewConversation={
            currentView === 'list' ? undefined : handleNewConversation
          }
          conversationTitle={conversationTitle}
          conversationId={messageThread.conversationId}
        />

        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {currentView === 'list' ? (
              <Box
                key="list"
                component={m.div}
                variants={slideVariants}
                initial="enterFromLeft"
                animate="center"
                exit="exitToLeft"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ConversationList
                  onSelectConversation={handleSelectConversation}
                  onNewConversation={handleNewConversation}
                />
              </Box>
            ) : (
              <Box
                key="conversation"
                component={m.div}
                variants={slideVariants}
                initial="enterFromRight"
                animate="center"
                exit="exitToRight"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ConversationView
                  messageThread={messageThread}
                  setMessageThread={setMessageThread}
                  onTitleChange={setConversationTitle}
                  initialPrompt={seededPrompt}
                  initialPromptVersion={seededPromptVersion}
                  pageContext={pageContext}
                />
              </Box>
            )}
          </AnimatePresence>
        </Box>
      </FloatingCard>
    </>
  );
}
